import React, { useState } from 'react';
import { Badge, Box, Button, Flex, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GearSix, Path, Plus, Trash } from 'phosphor-react';

import { IDataNoPagination, IPagination } from '../../../types/shared';
import { DataTable } from '../../../components/datatable';
import { TableBox } from '../../../components/tableBox';
import { EmptyData } from '../../../components/emptyData';
import { ActionsButton } from '../../../components/buttons/ActionsButton';
import { colors } from '../../../theme/colors';
import { useDialog } from '../../../providers/dialog';
import { useExclusaoPendente } from '../../../providers/exclusaoPendente';
import { IApiRecurso, useApiController } from '../../../hooks/useApiController';
import { RotaFormModal } from './RotaFormModal';
import { RotaDetalhesModal } from './RotaDetalhesModal';

export function RotasTab() {
  const queryClient = useQueryClient();
  const { getRecursos } = useApiController();
  const showDialog = useDialog();
  const { agendarExclusaoRota, isPendente } = useExclusaoPendente();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [detalhes, setDetalhes] = useState<IApiRecurso | null>(null);

  const { data, isFetching, isError, refetch } = useQuery<IDataNoPagination<IApiRecurso[]>>({
    queryKey: ['api-controller-recursos'],
    queryFn: getRecursos,
  });

  const items = data?.data ?? [];

  const pagination: IPagination = {
    page: 1,
    pages: 1,
    size: items.length,
    total: items.length,
    next_page: null,
    previous_page: null,
  };

  const columns: ColumnDef<IApiRecurso>[] = [
    {
      accessorKey: 'nome',
      header: 'Rota',
      cell: ({ row }) => (
        <Flex align="center" gap={2}>
          <Path size={18} color={colors.primary} />
          <Box>
            <Text fontWeight="medium">{row.original.nome}</Text>
            <Text fontSize="xs" color="gray.400">
              /external/v1/{row.original.slug}/…
            </Text>
          </Box>
        </Flex>
      ),
    },
    {
      accessorKey: 'tabelas',
      header: 'Tabelas de acesso',
      cell: ({ row }) => {
        const tabelas = row.original.tabelas ?? [];
        if (tabelas.length === 0) {
          return <Badge colorScheme="purple">{row.original.tabela_fonte}</Badge>;
        }
        const LIMITE = 2;
        const visiveis = tabelas.slice(0, LIMITE);
        const restantes = tabelas.length - visiveis.length;
        return (
          <Flex gap={1} align="center" maxW="260px">
            {visiveis.map(t => (
              <Badge
                key={t.tabela_fonte}
                colorScheme="purple"
                maxW="140px"
                isTruncated
                title={t.tabela_fonte}
              >
                {t.tabela_fonte}
              </Badge>
            ))}
            {restantes > 0 && (
              <Badge
                colorScheme="gray"
                variant="outline"
                cursor="pointer"
                flexShrink={0}
                onClick={() => setDetalhes(row.original)}
              >
                …+{restantes}
              </Badge>
            )}
          </Flex>
        );
      },
    },
    {
      accessorKey: 'ativo',
      header: 'Status',
      cell: ({ row }) =>
        isPendente(row.original.id) ? (
          <Badge colorScheme="red">excluindo…</Badge>
        ) : (
          <Badge colorScheme={row.original.ativo ? 'green' : 'gray'}>
            {row.original.ativo ? 'ativa' : 'inativa'}
          </Badge>
        ),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const item = row.original;
        if (isPendente(item.id)) return null;
        return (
          <ActionsButton aria-label="actions">
            <Button
              variant="unstyled"
              p="6px"
              display="flex"
              alignItems="center"
              gap={2}
              onClick={() => setDetalhes(item)}
            >
              <GearSix size={20} weight="bold" />
              <Text>Configurações</Text>
            </Button>
            <Button
              variant="unstyled"
              p="6px"
              display="flex"
              alignItems="center"
              gap={2}
              onClick={() =>
                showDialog({
                  status: 'warning',
                  title: 'Excluir esta rota?',
                  description: `${item.nome} — ao confirmar, a rota entra em exclusão (2 min para desfazer) e TODOS os tokens dela serão revogados.`,
                  cancelLabel: 'Cancelar',
                  onConfirm() {
                    agendarExclusaoRota(item.id, item.nome);
                  },
                })
              }
            >
              <Trash size={20} weight="bold" />
              <Text>Excluir</Text>
            </Button>
          </ActionsButton>
        );
      },
    },
  ];

  if (isError) {
    return (
      <Flex direction="column" align="center" gap={2} py={10}>
        <Text fontWeight="medium" color="red.500">
          Falha ao carregar as rotas
        </Text>
        <Button size="sm" onClick={() => refetch()}>
          Recarregar
        </Button>
      </Flex>
    );
  }

  return (
    <Box>
      {items.length === 0 && !isFetching ? (
        <EmptyData
          title="Nenhuma rota cadastrada"
          subtitle="Crie uma rota para definir quais tabelas a API pública serve"
          buttonLabel="Criar rota"
          onClick={() => setIsFormOpen(true)}
          icon={<Path size={90} color={colors.primary} />}
        />
      ) : (
        <TableBox>
          <Flex justify="flex-end">
            <Button leftIcon={<Plus weight="bold" />} onClick={() => setIsFormOpen(true)}>
              Criar rota
            </Button>
          </Flex>

          <div className="border border-b-divider" />

          <DataTable<IApiRecurso>
            columns={columns}
            data={items}
            pagination={pagination}
            emptyMsg="Nenhuma rota encontrada."
            loadingMsg="Carregando rotas..."
            isFetching={isFetching}
          />
        </TableBox>
      )}

      <RotaFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ['api-controller-recursos'] })}
      />

      <RotaDetalhesModal recurso={detalhes} onClose={() => setDetalhes(null)} />
    </Box>
  );
}
